import { For, Show, createEffect, createSignal } from "solid-js";

import {
  useArchiveCustomObject,
  useCreateCustomObject,
  useCustomObjects,
  useUpdateCustomObject,
} from "~/lib/hooks";
import type {
  CustomObjectDefinitionCreate,
  CustomObjectDefinitionRead,
  CustomObjectDefinitionUpdate,
} from "~/lib/types";

type Props = {
  wsKey: () => string;
};

type EditState = { mode: "add" } | { mode: "edit"; row: CustomObjectDefinitionRead };

type FormState = {
  key: string;
  label_singular: string;
  label_plural: string;
  description: string;
  name_template: string;
};

const blankForm = (): FormState => ({
  key: "",
  label_singular: "",
  label_plural: "",
  description: "",
  name_template: "",
});

export function CustomObjectsPanel(props: Props) {
  const [showArchived, setShowArchived] = createSignal(false);
  const objects = useCustomObjects(props.wsKey, () => ({
    include_archived: showArchived() || undefined,
  }));
  const createMutation = useCreateCustomObject(props.wsKey);
  const updateMutation = useUpdateCustomObject(props.wsKey);
  const archiveMutation = useArchiveCustomObject(props.wsKey);

  const [edit, setEdit] = createSignal<EditState | null>(null);
  const [form, setForm] = createSignal<FormState>(blankForm());
  const [error, setError] = createSignal<string | null>(null);

  // Hydrate the form when opening edit (or reset for add).
  createEffect(() => {
    const s = edit();
    if (!s) return;
    setError(null);
    if (s.mode === "add") {
      setForm(blankForm());
    } else {
      setForm({
        key: s.row.key,
        label_singular: s.row.label_singular,
        label_plural: s.row.label_plural,
        description: s.row.description ?? "",
        name_template: s.row.name_template ?? "",
      });
    }
  });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const f = form();
    if (!f.key.trim() || !f.label_singular.trim() || !f.label_plural.trim()) return;
    const state = edit();
    if (!state) return;
    try {
      if (state.mode === "add") {
        const payload: CustomObjectDefinitionCreate = {
          key: f.key.trim(),
          label_singular: f.label_singular.trim(),
          label_plural: f.label_plural.trim(),
          description: f.description.trim() || null,
          name_template: f.name_template.trim() || null,
        };
        await createMutation.mutateAsync(payload);
      } else {
        const payload: CustomObjectDefinitionUpdate = {
          label_singular: f.label_singular.trim(),
          label_plural: f.label_plural.trim(),
          description: f.description.trim() || null,
          name_template: f.name_template.trim() || null,
        };
        await updateMutation.mutateAsync({
          codId: state.row.id,
          body: payload,
          version: state.row.version,
        });
      }
      setEdit(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 class="card-title text-base">Custom objects</h3>
            <p class="text-xs opacity-60 mt-0.5">
              Workspace-declared entity types. The records layer ships next — for now you can
              declare the metadata.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <label class="label cursor-pointer gap-2">
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                checked={showArchived()}
                onChange={(e) => setShowArchived(e.currentTarget.checked)}
              />
              <span class="label-text text-sm">Show archived</span>
            </label>
          </div>
        </div>

        <Show when={objects.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-6 opacity-60">
            Loading…
          </div>
        </Show>

        <div class="overflow-x-auto mt-2">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Singular</th>
                <th>Plural</th>
                <th>Key</th>
                <th>Description</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <For each={objects.data?.items ?? []}>
                {(co) => {
                  const isArchived = co.archived_at !== null;
                  return (
                    <tr class={isArchived ? "opacity-50" : ""}>
                      <td class="font-medium">{co.label_singular}</td>
                      <td>{co.label_plural}</td>
                      <td class="text-xs opacity-60">
                        <code>{co.key}</code>
                      </td>
                      <td class="text-xs opacity-70 max-w-md">
                        <Show when={co.description} fallback={<span class="opacity-50">—</span>}>
                          <span class="line-clamp-2">{co.description}</span>
                        </Show>
                      </td>
                      <td class="text-right whitespace-nowrap">
                        <Show when={!isArchived}>
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs"
                            onClick={() => setEdit({ mode: "edit", row: co })}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs text-error"
                            onClick={() =>
                              archiveMutation.mutate({
                                codId: co.id,
                                version: co.version,
                              })
                            }
                          >
                            Archive
                          </button>
                        </Show>
                        <Show when={isArchived}>
                          <span class="badge badge-ghost badge-sm">archived</span>
                        </Show>
                      </td>
                    </tr>
                  );
                }}
              </For>
              <Show when={(objects.data?.items.length ?? 0) === 0 && !objects.isLoading}>
                <tr>
                  <td colspan={5} class="text-center py-6 opacity-60 text-sm">
                    No custom objects yet. Click "Add" to declare one (e.g. "Asset",
                    "Subscription").
                  </td>
                </tr>
              </Show>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end mt-3">
          <button
            type="button"
            class="btn btn-primary btn-sm"
            onClick={() => setEdit({ mode: "add" })}
          >
            Add custom object
          </button>
        </div>

        <Show when={edit()}>
          {(state) => (
            <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="co-title">
              <div class="modal-box">
                <h3 id="co-title" class="font-bold text-lg mb-4">
                  {state().mode === "add"
                    ? "Add custom object"
                    : `Edit ${state().mode === "edit" ? (state() as { row: CustomObjectDefinitionRead }).row.label_singular : ""}`}
                </h3>
                <form onSubmit={handleSubmit} class="flex flex-col gap-3">
                  <div>
                    <label class="label">
                      <span class="label-text">Key (URL slug, immutable)</span>
                    </label>
                    <input
                      class="input input-bordered w-full"
                      placeholder="asset"
                      aria-label="Key"
                      pattern="[a-z0-9][a-z0-9_]*"
                      maxlength="63"
                      value={form().key}
                      onInput={(e) => update("key", e.currentTarget.value)}
                      disabled={state().mode === "edit"}
                      required
                    />
                  </div>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="label">
                        <span class="label-text">Singular label</span>
                      </label>
                      <input
                        class="input input-bordered w-full"
                        placeholder="Asset"
                        aria-label="Singular label"
                        maxlength="120"
                        value={form().label_singular}
                        onInput={(e) => update("label_singular", e.currentTarget.value)}
                        required
                      />
                    </div>
                    <div>
                      <label class="label">
                        <span class="label-text">Plural label</span>
                      </label>
                      <input
                        class="input input-bordered w-full"
                        placeholder="Assets"
                        aria-label="Plural label"
                        maxlength="120"
                        value={form().label_plural}
                        onInput={(e) => update("label_plural", e.currentTarget.value)}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">Description (optional)</span>
                    </label>
                    <textarea
                      class="textarea textarea-bordered w-full"
                      placeholder="What does this object represent?"
                      aria-label="Description"
                      value={form().description}
                      onInput={(e) => update("description", e.currentTarget.value)}
                    />
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">Display name template (optional)</span>
                    </label>
                    <input
                      type="text"
                      class="input input-bordered w-full"
                      placeholder="e.g. {{sku}} — {{name}}"
                      aria-label="Display name template"
                      value={form().name_template}
                      onInput={(e) => update("name_template", e.currentTarget.value)}
                    />
                    <p class="text-xs opacity-60 mt-1">
                      Substitutes <code>{"{{field_key}}"}</code> from each record's custom fields.{" "}
                      <code>{"{{name}}"}</code> resolves to the record's name. Leave blank to use
                      the record's name as-is.
                    </p>
                  </div>

                  <Show when={error()}>
                    <div role="alert" class="alert alert-error text-sm">
                      {error()}
                    </div>
                  </Show>

                  <div class="modal-action">
                    <button type="button" class="btn btn-ghost" onClick={() => setEdit(null)}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      {state().mode === "add" ? "Add" : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </dialog>
          )}
        </Show>
      </div>
    </div>
  );
}
